import { useState } from "react";
import "../../userscreen/style.css";
import { useNavigate } from "react-router-dom";
import { Button, Form, Input, message } from "antd";
import { Container, Row, Col } from "react-bootstrap";
import PageHeader from "../../../component/pageHeaders";
import { API } from "../../../config/API";
import axios from "../../../config/axiosInstance";
import { useDispatch, useSelector } from "react-redux";
import { logout } from "../../../redux/slices/userslice";

const PASSWORD_RULE =
  /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+[\]{};':"\\|,.<>?]).{8,}$/;

function ChangePassword() {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useSelector((state: any) => state?.User?.user);
  const userId = user?.id;

  const onFinish = async (values: any) => {
    if (!userId) {
      message.error("Session expired. Please login again.");
      return;
    }

    setLoading(true);
    try {
      const url = `${API.BASE_URL}${API.CHANGE_PASSWORD}${userId}`;
      await axios.post(url, {
        currentPassword: values.currentPassword,
        newPassword: values.newPassword,
      });

      message.success(
        "Password updated successfully. Please log in again with your new password."
      );
      form.resetFields();
      dispatch(logout(""));
      navigate("/");
    } catch (error: any) {
      const apiMessage =
        error?.response?.data?.error?.message ||
        error?.response?.data?.message ||
        error?.message ||
        "Failed to change password. Please try again.";
      const isCurrentWrong = /current password is incorrect/i.test(apiMessage);

      if (isCurrentWrong) {
        form.setFields([
          {
            name: "currentPassword",
            errors: ["Current password is incorrect"],
          },
        ]);
      }

      message.error(apiMessage);
    } finally {
      setLoading(false);
    }
  };

  const validateNewPassword = (_: any, value: string) => {
    if (!value) {
      return Promise.reject(new Error("Please input your new password"));
    }
    if (!PASSWORD_RULE.test(value)) {
      return Promise.reject(
        new Error(
          "Min 8 chars, including uppercase, lowercase, number & special char"
        )
      );
    }
    return Promise.resolve();
  };

  return (
    <div>
      <PageHeader
        title={"Change Password"}
        length={`Dashboard / Profile / Change Password`}
      />
      <Form
        form={form}
        onFinish={onFinish}
        name="change-password"
        autoComplete="off"
        scrollToFirstError
      >
        <Container fluid>
          <Row style={{ padding: 10 }}>
            <Col sm={6} xs={12}>
              <Row>
                <Col sm="6" xs="12">
                  <label className="newsFormLabels">Current Password</label>
                  <Form.Item
                    name="currentPassword"
                    rules={[
                      {
                        required: true,
                        message: "Please input your current password",
                      },
                    ]}
                    hasFeedback
                  >
                    <Input.Password
                      size="large"
                      placeholder="Enter current password"
                      autoComplete="current-password"
                    />
                  </Form.Item>

                  <label className="newsFormLabels">New Password</label>
                  <Form.Item
                    name="newPassword"
                    rules={[{ validator: validateNewPassword }]}
                    dependencies={["currentPassword"]}
                    hasFeedback
                  >
                    <Input.Password
                      size="large"
                      placeholder="Enter new password"
                      autoComplete="new-password"
                    />
                  </Form.Item>
                </Col>

                <Col sm="6" xs="12">
                  <label className="newsFormLabels">Confirm New Password</label>
                  <Form.Item
                    name="confirmPassword"
                    dependencies={["newPassword"]}
                    rules={[
                      {
                        required: true,
                        message: "Please confirm your new password",
                      },
                      ({ getFieldValue }) => ({
                        validator(_, value) {
                          if (!value || getFieldValue("newPassword") === value) {
                            return Promise.resolve();
                          }
                          return Promise.reject(
                            new Error("Passwords do not match")
                          );
                        },
                      }),
                    ]}
                    hasFeedback
                  >
                    <Input.Password
                      size="large"
                      placeholder="Re-enter new password"
                      autoComplete="new-password"
                    />
                  </Form.Item>
                </Col>
              </Row>
            </Col>

            <Col sm={4} xs={12}>
              <div className="userfrom-box">
                <label className="newsFormLabels" style={{ marginBottom: 0 }}>
                  Password Requirements
                </label>
                <ul
                  style={{
                    padding: "10px 0 0 18px",
                    color: "#555",
                    fontSize: 13,
                    lineHeight: 1.8,
                    margin: 0,
                  }}
                >
                  <li>At least 8 characters</li>
                  <li>One uppercase letter (A–Z)</li>
                  <li>One lowercase letter (a–z)</li>
                  <li>One number (0–9)</li>
                  <li>One special character (!@#$…)</li>
                  <li>Must differ from current password</li>
                </ul>
              </div>
            </Col>
          </Row>
        </Container>
        <div style={{ height: "9vh" }} />
        <div className="FixedFooter">
          <Container fluid>
            <Row>
              <Col sm={9} xs={12}></Col>
              <Col sm={1} xs={6}>
                <Button
                  size="large"
                  type="text"
                  danger
                  block
                  onClick={() => navigate(-1)}
                  disabled={loading}
                >
                  Cancel
                </Button>
              </Col>
              <Col sm={2} xs={6}>
                <Button
                  block
                  type="primary"
                  htmlType="submit"
                  size="large"
                  loading={loading}
                >
                  Update Password
                </Button>
              </Col>
            </Row>
          </Container>
        </div>
      </Form>
    </div>
  );
}

export default ChangePassword;
